import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  Activity, BarChart3, CircleDollarSign, ClipboardCheck, Database, Download, GitMerge, Layers, LayoutDashboard, LogOut,
  ReceiptText, ScanSearch, ShieldCheck, Sparkles, Store, UserCheck, Users, type LucideIcon,
} from 'lucide-react';
import { FilterBar } from './FilterBar';
import { DateRangeSelector } from './DateRangeSelector';
import { useDashboard } from '../context/DashboardContext';
import { useAuth } from '../auth/AuthProvider';
import { formatRange } from '../utils/dateRange';
import { ExportControl } from '../export/ExportControl';
import { useV2Context } from '../v2/V2Context';
import type { Permission } from '../types/v2';
import { LanguageSwitcher, t, useI18n, type MessageKey } from '../i18n';

interface NavItem { to: string; label: MessageKey; icon: LucideIcon; permission: Permission | Permission[]; end?: boolean }

/** The eight sections. A role sees only what its permissions open; the API enforces the same rules on every request. */
export const SECTIONS: Array<{ title: MessageKey; items: NavItem[] }> = [
  { title: 'nav.group.executive', items: [
    { to: '/', label: 'nav.executive', icon: LayoutDashboard, permission: 'product.read', end: true },
    { to: '/summary', label: 'nav.summary', icon: BarChart3, permission: 'product.read' },
  ] },
  { title: 'nav.group.users', items: [{ to: '/users', label: 'nav.users', icon: Users, permission: 'users.read' }] },
  { title: 'nav.group.acquisition', items: [
    { to: '/acquisition', label: 'nav.acquisition', icon: Download, permission: 'product.read' },
    { to: '/activation', label: 'nav.activation', icon: GitMerge, permission: 'product.read', end: true },
    { to: '/activation/accounts', label: 'nav.accountActivation', icon: UserCheck, permission: 'users.read' },
  ] },
  { title: 'nav.group.engagement', items: [
    { to: '/engagement', label: 'nav.engagement', icon: Layers, permission: 'product.read' },
    { to: '/retention', label: 'nav.retention', icon: Activity, permission: 'product.read', end: true },
    { to: '/retention/accounts', label: 'nav.accountRetention', icon: Activity, permission: 'users.read' },
  ] },
  { title: 'nav.group.features', items: [
    { to: '/features', label: 'nav.features', icon: Sparkles, permission: 'product.read' },
    { to: '/receipts', label: 'nav.receipts', icon: ReceiptText, permission: 'product.read' },
    { to: '/price-search', label: 'nav.priceSearch', icon: ScanSearch, permission: 'product.read' },
  ] },
  { title: 'nav.group.retail', items: [{ to: '/retail', label: 'nav.retail', icon: Store, permission: 'retail.read' }] },
  { title: 'nav.group.exports', items: [{ to: '/exports', label: 'nav.exports', icon: Database, permission: ['exports.internal', 'exports.commercial'] }] },
  { title: 'nav.group.system', items: [
    { to: '/reliability', label: 'nav.reliability', icon: ShieldCheck, permission: 'product.read' },
    { to: '/costs', label: 'nav.costs', icon: CircleDollarSign, permission: 'product.read' },
  ] },
];

/** Pages that read the v1 device data source, where the scope and segment filters and the PDF/CSV report apply. */
const V1_PAGES = new Set(['/summary', '/acquisition', '/activation', '/engagement', '/retention', '/reliability']);

export function useAllowed() {
  const { can } = useV2Context();
  return (permission: Permission | Permission[]) => (Array.isArray(permission) ? permission : [permission]).some((item) => can(item));
}

export function Layout() {
  const { dataSource, range } = useDashboard();
  const { lang } = useI18n();
  const { displayName, isDevBypass, logout, role } = useAuth();
  const allowed = useAllowed();
  const { pathname } = useLocation();
  const v1 = V1_PAGES.has(pathname);
  const sections = SECTIONS.map((section) => ({ ...section, items: section.items.filter((item) => allowed(item.permission)) })).filter((section) => section.items.length);
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand"><img src="/aruha-mark.png" alt="" /><div><strong>Aruha</strong><span>Analytics</span></div></div>
        <nav aria-label={t('nav.sections')}>
          {sections.map((section) => <div key={section.title} className="nav-section">
            {(section.items.length > 1 || t(section.items[0]!.label) !== t(section.title)) && <span className="nav-group">{t(section.title)}</span>}
            {section.items.map(({ to, label, icon: Icon, end }) => <NavLink key={to} to={to} end={end}><Icon size={18} /><span>{t(label)}</span></NavLink>)}
          </div>)}
        </nav>
        <div className="sidebar-footer">
          <div className="privacy-note"><ClipboardCheck size={16} /><span>{role === 'admin' ? t('nav.privacyAdmin') : t('nav.privacyOther')}</span></div>
          <div className="admin-identity"><span>{displayName}{role && <small className="role-badge">{t(`common.role.${role}`)}</small>}</span><button aria-label={t('nav.signOut')} onClick={() => void logout()}><LogOut size={16} /></button></div>
        </div>
      </aside>
      <main className="main-content">
        <header className="topbar">
          <div className="mobile-brand"><img src="/aruha-mark.png" alt="Aruha" /><strong>Analytics</strong></div>
          <div className="source-row">
            {v1 && dataSource.kind === 'mock' && <span className="mock-badge">{t('nav.mockBadge')}</span>}
            {dataSource.kind === 'api' && <span className="api-badge">{t('nav.apiBadge')}</span>}
            {isDevBypass && <span className="dev-badge">{t('nav.devBadge')}</span>}
            <span className="range-summary">{formatRange(range)} · {range.timezone}</span>
          </div>
          <LanguageSwitcher />
          {role !== 'commercial' && <DateRangeSelector />}
          {v1 && <ExportControl />}
          {v1 && <FilterBar />}
        </header>
        {/* Keyed by language so every page re-renders its text and formats after a switch. */}
        <div className="page-container" key={lang}><Outlet /></div>
      </main>
    </div>
  );
}

export function PageHeader({ title, description }: { title: string; description: string }) {
  return <header className="page-header"><div><h1>{title}</h1><p>{description}</p></div></header>;
}
